'use client';

import { Icons } from '#/components/icons';
import { Button } from '#/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '#/components/ui/select';

import {
  Tooltip as TooltipRoot,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '#/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

import { Dispatch, SetStateAction } from 'react';
import { SelectItemType } from 'types/common';
import { trackEvent } from '#/lib/analytics';

export default function SelectTexts({
  localTexts,
  setLocalTexts,
  text,
  setText
}: {
  localTexts: SelectItemType[];
  setLocalTexts: (text: SelectItemType[]) => void;
  text: string;
  setText: Dispatch<SetStateAction<string>>;
}) {
  return (
    <div className="flex flex-col gap-2 w-full">
      {localTexts.length > 0 && (
        <>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <p>
                지난 입력 {localTexts.length > 0 && `(${localTexts.length})`}
              </p>
              {/* 기록이 이 브라우저에만 남는다는 점을 모르면, 기기를 옮겼을 때
                  사라진 것으로 오해한다. */}
              <TooltipProvider delayDuration={200}>
                <TooltipRoot>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="지난 입력 안내"
                      className="rounded-full p-0.5 text-muted-foreground/70 transition hover:bg-accent hover:text-foreground"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="max-w-[min(22rem,85vw)] text-xs leading-relaxed"
                  >
                    이미 입력한 문장을 다시 입력할 수 있습니다. 여기에 기록된
                    정보는 브라우저가 기억하고 있습니다. 다른 브라우저를 쓰거나
                    다른 기기에서 접속하시면 내용이 보이지 않습니다.
                  </TooltipContent>
                </TooltipRoot>
              </TooltipProvider>
            </div>
            <Button
              variant={'ghost'}
              onClick={() => {
                trackEvent('use_recent_text', {
                  action: 'clear_all',
                  saved_count: localTexts.length
                });
                setLocalTexts([]);
              }}
            >
              기록 지우기
            </Button>
          </div>
          <Select
            value={text}
            onValueChange={(val) => {
              // GA4: '지난 입력' 재사용. 이 기능이 실제로 재방문·반복 사용에
              // 기여하는지를 보려는 지표다(기록은 브라우저 localStorage 에만 있다).
              trackEvent('use_recent_text', {
                action: 'select',
                saved_count: localTexts.length,
                text_length: val.length
              });
              setText(val);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="문장 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectGroup>
                  {localTexts
                    .filter(({ value }) => value !== '')
                    .map(({ label, value }, index) => (
                      <div className="relative" key={index}>
                        <SelectItem key={value} value={value}>
                          <div className="flex w-full items-center">
                            <span className="truncate">
                              {label.length > 15
                                ? `${label.slice(0, 15)}...`
                                : label}
                            </span>
                          </div>
                        </SelectItem>
                        <button
                          tabIndex={-1}
                          onClick={() => {
                            setLocalTexts(
                              localTexts.filter((item) => item.value !== value)
                            );
                          }}
                          className=" absolute right-14 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        >
                          <Icons.circleX className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                </SelectGroup>
              </SelectGroup>
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  );
}
